/* eslint-disable react-hooks/exhaustive-deps */
import { useAtom, useSetAtom } from "jotai";
import React, { MutableRefObject, RefObject, useEffect } from "react";
import styled from "styled-components";
import { Background } from "../game-object/Background";
import { BigMonster } from "../game-object/BigMonster";
import { Floor } from "../game-object/Floor";
import { Hero } from "../game-object/Hero";
import { Monster } from "../game-object/Monster";
import { Sky } from "../game-object/Sky";
import { Star } from "../game-object/Star";
import { endStateAtom, newStarCountAtom, scoreAtom } from "../jotai/game";

interface Props {
  canvasRef: RefObject<HTMLCanvasElement>;
  requestRef: MutableRefObject<number | undefined>;
  ctxRef: MutableRefObject<CanvasRenderingContext2D | undefined>;
  monsterListRef: MutableRefObject<Monster[] | BigMonster[]>;
  heroRef: MutableRefObject<Hero | undefined>;
  timerRef: MutableRefObject<number>;
  floorRef: MutableRefObject<Floor | undefined>;
  skyRef: MutableRefObject<Sky | undefined>;
  isEndRef: MutableRefObject<boolean>;
  scoreRef: MutableRefObject<number>;
  starListRef: MutableRefObject<Star[]>;
  starCountRef: MutableRefObject<number>;
  backgroundRef: MutableRefObject<Background | undefined>;
  stageRef: MutableRefObject<number>;
}

const MONSTER_WIDTH = 40;
const BIG_MONSTER_WIDTH = 80;
const STAGE_PER_SCORE = 500;

const Game = ({
  canvasRef,
  requestRef,
  ctxRef,
  monsterListRef,
  heroRef,
  timerRef,
  floorRef,
  skyRef,
  isEndRef,
  scoreRef,
  starListRef,
  starCountRef,
  backgroundRef,
  stageRef,
}: Props) => {
  const [endState, setEndState] = useAtom(endStateAtom);
  const setScore = useSetAtom(scoreAtom);
  const setNewStarCount = useSetAtom(newStarCountAtom);

  const draw = () => {
    timerRef.current += 1;
    const timer = timerRef.current;

    const ctx = ctxRef.current;
    const monsterList = monsterListRef.current;
    const hero = heroRef.current;
    const floor = floorRef.current;
    const sky = skyRef.current;
    const isEnd = isEndRef.current;
    const score = scoreRef.current;
    const starList = starListRef.current;
    const starCount = starCountRef.current;
    const background = backgroundRef.current;
    const stage = stageRef.current;

    // console.log(monsterList);
    const canvas = canvasRef.current;

    if (
      monsterList &&
      ctx &&
      canvas &&
      hero &&
      floor &&
      sky &&
      starList &&
      background
    ) {
      // 지우기
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 하늘 그리기
      // sky.draw(ctx);
      // 바닥 그리기
      floor.draw(ctx);
      // 배경 그리기
      background.draw(ctx);

      // 게임 중이면 점수 올리기
      if (!isEnd) scoreRef.current = timer;

      // 스테이지 설정
      stageRef.current = Math.floor(score / STAGE_PER_SCORE) + 1;

      /**
       * 똥 만들기
       * 스테이지 9 이상 똥출현 빈도 높이기
       */
      const makeMonsterPeriod = stage >= 9 ? 10 : 12;
      if (timer % makeMonsterPeriod === 0 && !isEnd) {
        const randomX = Math.floor(
          Math.random() * (canvas.width - MONSTER_WIDTH)
        );
        const monster = new Monster(randomX);

        monsterListRef.current = [
          ...monsterList.filter(
            (monster) => monster.y + monster.height <= canvas.height
          ),
          monster,
        ];
      }

      /**
       *
       * 5% 확률로 생성
       * 큰 똥 만들기
       * 4스테이지 부터 5% 확률로 생성
       * 6스테이지 부터 10% 확률로 생성
       */
      const randomInner =
        stage >= 6
          ? Math.floor(Math.random() * 10)
          : Math.floor(Math.random() * 20);
      if (timer % 10 === 0 && !isEnd && randomInner === 5 && stage >= 4) {
        const randomX = Math.floor(
          Math.random() * (canvas.width - BIG_MONSTER_WIDTH)
        );
        const monster = new BigMonster(randomX);

        monsterListRef.current = [
          ...monsterList.filter(
            (monster) => monster.y + monster.height <= canvas.height
          ),
          monster,
        ];
      }

      monsterList.forEach((monster) => {
        // 내려오기
        if (!isEnd) monster.down(stage);

        // 충돌 조건
        if (
          monster.hitboxY + monster.hitboxHeight >= hero.hitboxY &&
          monster.hitboxY + monster.hitboxHeight <=
            hero.hitboxY + hero.hitboxHeight &&
          ((monster.hitboxX >= hero.hitboxX &&
            monster.hitboxX <= hero.hitboxX + hero.hitboxWidth) ||
            (monster.hitboxX + monster.hitboxWidth >= hero.hitboxX &&
              monster.hitboxX + monster.hitboxWidth <=
                hero.hitboxX + hero.hitboxWidth))
        ) {
          // console.log("충돌");
          // monsterListRef.current = [];
          timerRef.current = 0;
          isEndRef.current = true;
        }
        monster.draw(ctx);
      });

      /**
       * 10% 별 만들기
       */
      const randomInner10 = Math.floor(Math.random() * 10);
      if (timer % 10 === 0 && !isEnd && randomInner10 === 1) {
        const randomX = Math.floor(
          Math.random() * (canvas.width - MONSTER_WIDTH)
        );
        const star = new Star(randomX);

        starListRef.current = [
          ...starList.filter((star) => star.y + star.height <= canvas.height),
          star,
        ];
      }

      let deleteIndex = undefined;
      starList.forEach((star, idx) => {
        // 내려오기
        if (!isEnd) star.down();

        // 충돌 조건
        if (
          star.hitboxY + star.hitboxHeight >= hero.hitboxY &&
          star.hitboxY + star.hitboxHeight <=
            hero.hitboxY + hero.hitboxHeight &&
          ((star.hitboxX >= hero.hitboxX &&
            star.hitboxX <= hero.hitboxX + hero.hitboxWidth) ||
            (star.hitboxX + star.hitboxWidth >= hero.hitboxX &&
              star.hitboxX + star.hitboxWidth <=
                hero.hitboxX + hero.hitboxWidth))
        ) {
          console.log("별");
          deleteIndex = idx;
        }
        star.draw(ctx);
      });

      if (deleteIndex !== undefined) {
        console.log("별삭제,", deleteIndex);
        starList.splice(deleteIndex, 1);
        starCountRef.current++;
      }

      if (hero.status === "left" && !isEnd) hero.leftMove();
      else if (hero.status === "right" && !isEnd)
        hero.rightMove(canvas.width - hero.width);

      // 점수 렌더링
      ctx.font = "34px GangwonEduHyeonokT_OTFMediumA";
      ctx.fillStyle = "black";
      ctx.textAlign = "start";
      ctx.fillText(`점수: ${score}`, 10, 50);

      // stage 렌더링
      ctx.font = "34px GangwonEduHyeonokT_OTFMediumA";
      ctx.textAlign = "center";
      ctx.strokeStyle = "black";
      ctx.fillStyle = "white";
      ctx.strokeText(`stage ${stage}`, canvas.width / 2, 50);
      ctx.font = "37px GangwonEduHyeonokT_OTFMediumA";
      ctx.fillText(`stage ${stage}`, canvas.width / 2, 50);

      if (score % STAGE_PER_SCORE <= 100) {
        // 스테이지 전환 후 중간에 스테이지 그리기
        ctx.font = "37px GangwonEduHyeonokT_OTFMediumA";
        ctx.strokeText(`stage ${stage}`, canvas.width / 2, canvas.height / 2);
        ctx.font = "40px GangwonEduHyeonokT_OTFMediumA";
        ctx.fillText(`stage ${stage}`, canvas.width / 2, canvas.height / 2);
      }

      // hero 렌더링
      hero.draw(ctx);

      // 게임 종료
      if (isEnd) {
        setEndState(true);
        setScore(score);
        setNewStarCount(starCount);

        scoreRef.current = 0;
        starCountRef.current = 0;
        monsterListRef.current = [];
        starListRef.current = [];
        cancelAnimationFrame(requestRef.current as number);
      }
    }

    if (!isEnd) requestAnimationFrame(draw);
  };

  useEffect(() => {
    if (!endState) {
      const canvas = canvasRef.current;

      if (canvas) {
        canvas.width = 350;
        canvas.height = 600;

        const context = canvas.getContext("2d");

        if (context) {
          ctxRef.current = context;
        }
      }

      requestRef.current = requestAnimationFrame(draw);
    } else {
      isEndRef.current = false;
    }
  }, [endState]);

  useEffect(() => {
    return () => {
      cancelAnimationFrame(requestRef.current as number);
    };
  }, []);

  return <Canvas ref={canvasRef} />;
};

export default Game;

const Canvas = styled.canvas`
  width: 100%;
  height: 100%;
`;
